import { Injectable } from '@angular/core';

import { AccessibilityAuditToolNames } from '@shared/constants/audit-tool';
import { $auditFinding } from '@shared/constants/audit-finding';
import { $flaw } from '@shared/constants/flaw';
import { $scanIssues } from '@shared/constants/scan-issues';
import { MobileEngineScanOrigins, ScanOrigin } from '@shared/constants/scanning';
import { IAutomatedScanIssue, IScanIssues } from '@shared/interfaces/scan-issues.interface';
import { SharedCommonUtility } from '@shared/utils/common.utility';
import { SharedTextUtility } from '@shared/utils/text.utility';

import { IFindingContentBlock } from '@client/app/interfaces/finding.interface';

export interface IBuildIssueContentBlocksParams {
  issue: IAutomatedScanIssue;
  ruleDetails: IScanIssues;
  toolName: AccessibilityAuditToolNames;
  isMobile: boolean;
  description: string;
  selectorId: string;
  nodesHTMLId: string;
}

@Injectable({ providedIn: 'root' })
export class FindingContentBlockService {
  public buildIssueContentBlocks(params: IBuildIssueContentBlocksParams): IFindingContentBlock[] {
    const { issue, ruleDetails, toolName, isMobile, description, selectorId, nodesHTMLId } = params;
    const blocks: IFindingContentBlock[] = [];
    const isAccessEngine: boolean = toolName === AccessibilityAuditToolNames.access_engine;

    if (!isMobile && SharedCommonUtility.notNullish(issue?.data?.componentData?.componentName)) {
      blocks.push({
        dataTestId: 'component-data',
        labelKey: 'component_name',
        type: 'text',
        value: issue.data.componentData.componentName,
      });
    }

    if (description) {
      blocks.push({
        dataTestId: 'description',
        labelKey: 'audit_rule_property_description',
        type: 'highlighted',
        value: description,
        codeLanguage: 'html',
      });
    }

    if (ruleDetails[$scanIssues.whyItMatters]) {
      blocks.push({
        dataTestId: 'why-it-matters',
        labelKey: 'audit_rule_property_why_it_matters',
        type: isAccessEngine ? 'html' : 'highlighted',
        value: ruleDetails[$scanIssues.whyItMatters],
        codeLanguage: 'html',
      });
    }

    if (ruleDetails[$scanIssues.whatToDo]) {
      blocks.push({
        dataTestId: 'what-to-do',
        labelKey: 'audit_rule_property_what_to_do',
        type: isAccessEngine ? 'html' : 'highlighted',
        value: ruleDetails[$scanIssues.whatToDo],
        codeLanguage: 'html',
      });
    }

    if (issue.data.elementReference?.trim()?.length > 0) {
      blocks.push({
        dataTestId: 'location-code',
        labelKey: isMobile ? 'location' : 'label_xpath_or_css',
        type: 'code',
        value: issue.data.elementReference,
        codeLanguage: 'xquery',
        copyElementId: selectorId,
      });
    }

    if (issue.data.element) {
      blocks.push({
        dataTestId: 'element-code',
        labelKey: isMobile ? 'xml_element' : 'audit_rule_property_nodesHTML',
        type: 'code',
        value: issue.data.element,
        codeLanguage: 'html',
        copyElementId: nodesHTMLId,
      });
    }

    return blocks;
  }

  public buildMobileAppContentBlocks(
    issue: IAutomatedScanIssue,
    ruleDetails: IScanIssues,
    scanOrigin?: ScanOrigin,
  ): IFindingContentBlock[] {
    const blocks: IFindingContentBlock[] = [];

    if (ruleDetails?.[$scanIssues.whyItMatters]) {
      blocks.push({
        dataTestId: 'why-it-matters',
        labelKey: 'audit_rule_property_why_it_matters',
        type: 'html',
        value: SharedTextUtility.escapeAccessEngineHtmlFormat(ruleDetails[$scanIssues.whyItMatters]),
      });
    }

    if (ruleDetails?.[$scanIssues.whatToDo]) {
      blocks.push({
        dataTestId: 'what-to-do',
        labelKey: 'audit_rule_property_what_to_do',
        type: 'html',
        value: SharedTextUtility.escapeAccessEngineHtmlFormat(ruleDetails[$scanIssues.whatToDo]),
      });
    }

    if (issue?.[$flaw.data]?.[$auditFinding.elementReference] && !MobileEngineScanOrigins.includes(scanOrigin)) {
      blocks.push({
        dataTestId: 'mobile-issue-location',
        labelKey: 'location',
        type: 'code',
        value: issue[$flaw.data][$auditFinding.elementReference],
        codeLanguage: 'xquery',
        copyElementId: 'inline-mobile-issue-location',
      });
    }

    if (issue?.[$flaw.data]?.[$auditFinding.element]) {
      blocks.push({
        dataTestId: 'mobile-issue-element',
        labelKey: 'xml_element',
        type: 'code',
        value: issue[$flaw.data][$auditFinding.element],
        codeLanguage: 'html',
        copyElementId: 'inline-mobile-issue-element',
      });
    }

    return blocks;
  }
}
